import { useDispatch, useSelector } from "react-redux";
import { useState, useEffect } from "react";
import { MDBTable, MDBTableBody, MDBTableHead } from "mdb-react-ui-kit";
import { toast } from "react-toastify";
import Loader from "../../components/Loader";
import {
  fetchMilestones,
  createMilestone,
  updateMilestone,
} from "../../actions/referralActions";
import useHasPermission from "../../hooks/useHasPermission";
import { PERMISSIONS } from "../../constants/permissions";
import {
  modalOverlay,
  modalBox,
  modalHeader,
  modalBody,
  closeBtn,
} from "./shared";

const emptyForm = {
  name: "",
  description: "",
  target_referral_count: "",
  reward_points: "",
  is_active: true,
};

const inputStyle = {
  width: "100%",
  padding: "10px 12px",
  border: "1px solid #444",
  borderRadius: "6px",
  background: "#1a1a1a",
  color: "#fff",
  fontSize: "14px",
};

const labelStyle = {
  display: "block",
  fontSize: "12px",
  color: "#aaa",
  marginBottom: "4px",
  fontWeight: "500",
};

const Milestones = () => {
  const dispatch = useDispatch();
  const canManage = useHasPermission(PERMISSIONS.MANAGE_REFERRAL_MILESTONES);

  const [showModal, setShowModal] = useState(false);
  const [editingMilestone, setEditingMilestone] = useState(null);
  const [form, setForm] = useState(emptyForm);
  const [submitting, setSubmitting] = useState(false);
  const [showActiveOnly, setShowActiveOnly] = useState(false);

  const { loading, data, error } = useSelector((state) => state.getMilestones) || {};
  const { loading: createLoading } = useSelector((state) => state.createMilestone) || {};
  const { loading: updateLoading } = useSelector((state) => state.updateMilestone) || {};

  const milestones = data?.data;

  useEffect(() => {
    dispatch(fetchMilestones(showActiveOnly));
  }, [showActiveOnly]);

  const openCreate = () => {
    setEditingMilestone(null);
    setForm(emptyForm);
    setShowModal(true);
    document.body.style.overflow = "hidden";
  };

  const openEdit = (milestone) => {
    setEditingMilestone(milestone);
    setForm({
      name: milestone.name || "",
      description: milestone.description || "",
      target_referral_count: milestone.target_referral_count ?? "",
      reward_points: milestone.reward_points ?? "",
      is_active: milestone.is_active ?? true,
    });
    setShowModal(true);
    document.body.style.overflow = "hidden";
  };

  const closeModal = () => {
    setShowModal(false);
    setEditingMilestone(null);
    setForm(emptyForm);
    document.body.style.overflow = "auto";
  };

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setForm((prev) => ({ ...prev, [name]: type === "checkbox" ? checked : value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.name.trim()) { toast.error("Name is required"); return; }
    if (!form.target_referral_count) { toast.error("Target referral count is required"); return; }
    if (!form.reward_points) { toast.error("Reward points is required"); return; }

    setSubmitting(true);
    const body = {
      name: form.name.trim(),
      description: form.description.trim(),
      target_referral_count: Number(form.target_referral_count),
      reward_points: Number(form.reward_points),
      is_active: form.is_active,
    };

    try {
      if (editingMilestone) {
        await dispatch(updateMilestone(editingMilestone.id, body));
        toast.success("Milestone updated successfully");
      } else {
        await dispatch(createMilestone(body));
        toast.success("Milestone created successfully");
      }
      closeModal();
      dispatch(fetchMilestones(showActiveOnly));
    } catch (err) {
      toast.error(err?.message || "Operation failed");
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) return <Loader />;
  if (error) return <p className="text-danger text-center">{error}</p>;

  return (
    <>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "16px" }}>
        <label style={{ display: "flex", alignItems: "center", gap: "8px", fontSize: "14px", cursor: "pointer" }}>
          <input type="checkbox" checked={showActiveOnly} onChange={(e) => setShowActiveOnly(e.target.checked)} />
          Active only
        </label>
        {canManage && (
          <button onClick={openCreate} style={{ padding: "8px 18px", background: "#007bff", color: "#fff", border: "none", borderRadius: "6px", cursor: "pointer", fontWeight: "600", fontSize: "14px" }}>
            + Add Milestone
          </button>
        )}
      </div>

      <MDBTable responsive style={{ width: "100%", fontSize: "14px" }}>
        <MDBTableHead>
          <tr>
            <th>Name</th>
            <th>Description</th>
            <th>Target Referrals</th>
            <th>Reward Points</th>
            <th>Status</th>
            {canManage && <th>Action</th>}
          </tr>
        </MDBTableHead>
        <MDBTableBody>
          {milestones?.length > 0 ? (
            milestones.map((item) => (
              <tr key={item.id}>
                <td style={{ fontWeight: "600" }}>{item.name}</td>
                <td style={{ maxWidth: "280px", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{item.description || "-"}</td>
                <td>{item.target_referral_count}</td>
                <td>{item.reward_points}</td>
                <td>
                  <span style={{ padding: "3px 10px", borderRadius: "12px", fontSize: "12px", fontWeight: "600", background: item.is_active ? "#28a74533" : "#6c757d33", color: item.is_active ? "#28a745" : "#6c757d" }}>
                    {item.is_active ? "Active" : "Inactive"}
                  </span>
                </td>
                {canManage && (
                  <td>
                    <p className="withdrawal-btn" onClick={() => openEdit(item)} style={{ cursor: "pointer", color: "white", width: "70px", textAlign: "center", margin: 0 }}>
                      Edit
                    </p>
                  </td>
                )}
              </tr>
            ))
          ) : (
            <tr>
              <td colSpan={canManage ? "6" : "5"} style={{ textAlign: "center", padding: "20px" }}>No milestones found</td>
            </tr>
          )}
        </MDBTableBody>
      </MDBTable>

      {showModal && (
        <div style={modalOverlay} onClick={closeModal}>
          <div style={{ ...modalBox, maxWidth: "500px" }} className="referral-modal-box" onClick={(e) => e.stopPropagation()}>
            <div style={modalHeader} className="referral-modal-header">
              <h3 style={{ margin: 0, fontSize: "18px" }}>{editingMilestone ? "Edit Milestone" : "Create Milestone"}</h3>
              <button style={closeBtn} onClick={closeModal}>✕</button>
            </div>
            <div style={modalBody} className="referral-modal-body">
              <form onSubmit={handleSubmit}>
                <div style={{ display: "flex", flexDirection: "column", gap: "16px" }}>
                  <div>
                    <label style={labelStyle}>Name *</label>
                    <input name="name" value={form.name} onChange={handleChange} style={inputStyle} placeholder="e.g. First 5 Referrals" required />
                  </div>
                  <div>
                    <label style={labelStyle}>Description</label>
                    <textarea name="description" value={form.description} onChange={handleChange} style={{ ...inputStyle, minHeight: "80px", resize: "vertical" }} placeholder="Optional description" />
                  </div>
                  <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "12px" }}>
                    <div>
                      <label style={labelStyle}>Target Referral Count *</label>
                      <input type="number" name="target_referral_count" value={form.target_referral_count} onChange={handleChange} style={inputStyle} min="1" required />
                    </div>
                    <div>
                      <label style={labelStyle}>Reward Points *</label>
                      <input type="number" name="reward_points" value={form.reward_points} onChange={handleChange} style={inputStyle} min="0" required />
                    </div>
                  </div>
                  <div>
                    <label style={{ display: "flex", alignItems: "center", gap: "8px", fontSize: "14px", cursor: "pointer" }}>
                      <input type="checkbox" name="is_active" checked={form.is_active} onChange={handleChange} />
                      Active
                    </label>
                  </div>
                  <div style={{ display: "flex", gap: "12px", justifyContent: "flex-end", paddingTop: "8px" }}>
                    <button type="button" onClick={closeModal} style={{ padding: "10px 20px", border: "1px solid #444", borderRadius: "6px", background: "transparent", color: "#fff", cursor: "pointer" }}>
                      Cancel
                    </button>
                    <button type="submit" disabled={submitting || createLoading || updateLoading} style={{ padding: "10px 24px", background: "#007bff", color: "#fff", border: "none", borderRadius: "6px", cursor: submitting ? "not-allowed" : "pointer", fontWeight: "600", opacity: submitting ? 0.7 : 1 }}>
                      {submitting ? "Saving..." : editingMilestone ? "Update" : "Create"}
                    </button>
                  </div>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default Milestones;
